import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Loader2, User, AlertCircle, Eye, EyeOff, UserPlus, CheckCircle, RefreshCw, MoreVertical, UserCheck, ArrowRightLeft, Plus, Calendar, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DateRange } from 'react-day-picker';
import { apiService, ApiError, FieldError } from '../../services/api';
import { UserListItem, UserListPagination } from '../../types/api';
import { cn } from '../../lib/utils';
import { CreatedDateRangePicker } from '../admin/CreatedDateRangePicker';

interface UserListProps {
  onUserSelect: (user: UserListItem) => void;
  refreshTrigger?: number;
}

let backofficeVersionPromise: Promise<string> | null = null;
const getBackofficeVersion = async (): Promise<string> => {
  if (!backofficeVersionPromise) {
    backofficeVersionPromise = fetch('/version.txt', { cache: 'no-store' })
      .then(response => (response.ok ? response.text() : ''))
      .then(text => {
        const version = text.trim().split(/\r?\n/)[0]?.trim();
        return (
          version ||
          process.env.REACT_APP_VERSION ||
          process.env.REACT_APP_BUILD_VERSION ||
          'unknown'
        );
      })
      .catch(
        () =>
          process.env.REACT_APP_VERSION ||
          process.env.REACT_APP_BUILD_VERSION ||
          'unknown'
      );
  }

  return backofficeVersionPromise;
};

export function UserList({ onUserSelect, refreshTrigger = 0 }: UserListProps) {
  const REFERRAL_ID_PATTERN = /^[A-Za-z0-9-]+$/;
  const validateReferralId = (value?: string): string | null => {
    const trimmed = (value || '').trim();
    if (!trimmed) return null;
    if (trimmed.length > 64) return 'Referral ID must be 64 characters or less';
    if (!REFERRAL_ID_PATTERN.test(trimmed)) {
      return 'Referral ID can contain only letters, numbers, and dashes';
    }
    return null;
  };

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [pagination, setPagination] = useState<UserListPagination>({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleSSNs, setVisibleSSNs] = useState<Set<string>>(new Set());
  const [fullSSNs, setFullSSNs] = useState<Map<string, string>>(new Map());
  const [loadingSSNs, setLoadingSSNs] = useState<Set<string>>(new Set());
  
  // Filter states
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createdFilter, setCreatedFilter] = useState<string>('all');
  const [createdRange, setCreatedRange] = useState<DateRange | undefined>(undefined);
  const [isCreatedPickerOpen, setIsCreatedPickerOpen] = useState(false);

  const formatCreatedRangeLabel = (range?: DateRange) => {
    if (!range?.from && !range?.to) return 'Custom range';
    const formatDate = (date: Date) =>
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (range?.from && !range?.to) return formatDate(range.from);
    if (range?.from && range?.to) return `${formatDate(range.from)} - ${formatDate(range.to)}`;
    return 'Custom range';
  };
  
  // Dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  
  // CRS enrollment state
  const [enrollingUserId, setEnrollingUserId] = useState<string | null>(null);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState<Set<string>>(new Set());
  const [enrollmentError, setEnrollmentError] = useState<{ userId: string; message: string } | null>(null);
  
  // Migration state
  const [migratingUserId, setMigratingUserId] = useState<string | null>(null);
  const [migrationSuccess, setMigrationSuccess] = useState<Set<string>>(new Set());
  const [migrationError, setMigrationError] = useState<{ userId: string; message: string } | null>(null);
  
  // CRS registration dialog state
  const [showCRSDialog, setShowCRSDialog] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [registrationFieldErrors, setRegistrationFieldErrors] = useState<FieldError[]>([]);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<{
    idpass: boolean;
    requiresSMFA: boolean;
    smfaToken?: string;
    message?: string;
    smsMessage?: string;
  } | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [userAccessToken, setUserAccessToken] = useState<string | null>(null);
  
  // IdentityIQ registration dialog state
  const [showIdentityIQDialog, setShowIdentityIQDialog] = useState(false);
  const [isRegisteringIdentityIQ, setIsRegisteringIdentityIQ] = useState(false);
  const [identityIQError, setIdentityIQError] = useState<string | null>(null);
  const [identityIQFieldErrors, setIdentityIQFieldErrors] = useState<FieldError[]>([]);
  const [identityIQSuccess, setIdentityIQSuccess] = useState(false);
  const [identityIQExampleIndex, setIdentityIQExampleIndex] = useState(0);
  
  // IdentityIQ example data
  const identityIQExamples = [
    {
      firstName: 'HOT',
      lastName: 'CHILI',
      email: 'hot.chili@example.com',
      streetAddress: '3325 ARMORY RD',
      city: 'CASTLE HAYNE',
      state: 'NC',
      zip: '28430',
      ssn: '666525461',
      birthDate: '1/1/84',
    },
    {
      firstName: 'ED',
      lastName: 'HEAT',
      email: 'ed.heat@example.com',
      streetAddress: '4163 WHITE OAK DR',
      city: 'MISSOURI CITY',
      state: 'MO',
      zip: '64072',
      ssn: '666254741',
      birthDate: '1/1/84',
    },
    {
      firstName: 'REAPER',
      lastName: 'JOHN',
      email: 'reaper.john@example.com',
      streetAddress: '805 WALNUT STREET',
      city: 'JACKSON',
      state: 'MS',
      zip: '39202',
      ssn: '666254146',
      birthDate: '1/1/84',
    },
    {
      firstName: 'MANGO',
      lastName: 'HABI',
      email: 'mango.habi@example.com',
      streetAddress: '1523 CROWFIELD RD',
      city: 'PHOENIX',
      state: 'AZ',
      zip: '85018',
      ssn: '666154747',
      birthDate: '2/2/85',
    },
    {
      firstName: 'SWEET',
      lastName: 'HOT',
      email: 'sweet.hot@example.com',
      streetAddress: '3340 JOES RD',
      city: 'NIVERVILLE',
      state: 'NY',
      zip: '12130',
      ssn: '666154736',
      birthDate: '1/1/84',
    },
    {
      firstName: 'GORDON',
      lastName: 'LEDGER',
      email: 'gordon.ledger@example.com',
      streetAddress: '1266 US RTE ONE',
      city: 'AMIRY',
      state: 'ME',
      zip: '04471',
      ssn: '500009385',
      birthDate: '1/1/84',
    },
  ];
  
  // CRS example data
  const crsExamples = [
    {
      firstName: 'Test',
      lastName: 'User',
      email: 'test.user@example.com',
      streetAddress: '123 Test Street',
      city: 'Atlanta',
      state: 'GA',
      zip: '30316',
      ssn: '666106116',
      birthDate: '1990-01-01',
      phoneNumber: '5555551222',
    },
    {
      firstName: 'Bureau',
      lastName: 'One',
      email: 'bureau.one@example.com',
      streetAddress: '456 Bureau St',
      city: 'Atlanta',
      state: 'GA',
      zip: '30316',
      ssn: '666101000',
      birthDate: '1990-01-01',
      phoneNumber: '5555551201',
    },
    {
      firstName: 'Bureau',
      lastName: 'Three',
      email: 'bureau.three@example.com',
      streetAddress: '789 Bureau Ave',
      city: 'Atlanta',
      state: 'GA',
      zip: '30316',
      ssn: '666103000',
      birthDate: '1990-01-01',
      phoneNumber: '5555551203',
    },
  ];
  const [crsExampleIndex, setCrsExampleIndex] = useState(0);
  
  // Form data for new CRS user
  const [crsFormData, setCrsFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    ssn: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    phoneNumber: '',
    referralId: '',
    goal: 'improve_score',
    goalTargetPeriod: '6_months',
  });
  
  // Form data for new IdentityIQ user
  const [identityIQFormData, setIdentityIQFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    ssn: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    phoneNumber: '',
    referralId: '',
    goal: 'improve_score',
    goalTargetPeriod: '6_months',
  });

  const toUtcDayStartIso = (date: Date): string =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)).toISOString();

  const toUtcDayEndIso = (date: Date): string =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)).toISOString();

  const getCreatedDateRange = (): { from?: string; to?: string } => {
    const now = new Date();
    switch (createdFilter) {
      case 'today':
        return { from: toUtcDayStartIso(now), to: toUtcDayEndIso(now) };
      case 'week': {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { from: toUtcDayStartIso(weekAgo), to: toUtcDayEndIso(now) };
      }
      case 'month': {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return { from: toUtcDayStartIso(monthAgo), to: toUtcDayEndIso(now) };
      }
      case 'custom':
        if (createdRange?.from) {
          return {
            from: toUtcDayStartIso(createdRange.from),
            to: toUtcDayEndIso(createdRange.to ?? createdRange.from),
          };
        }
        return {};
      default:
        return {};
    }
  };

  const buildFilters = () => {
    const filters: {
      subscription?: string;
      provider?: string;
      status?: string;
      created_from?: string;
      created_to?: string;
    } = {};
    if (subscriptionFilter !== 'all') filters.subscription = subscriptionFilter;
    if (providerFilter !== 'all') filters.provider = providerFilter;
    if (statusFilter !== 'all') filters.status = statusFilter;
    const dateRange = getCreatedDateRange();
    if (dateRange.from) filters.created_from = dateRange.from;
    if (dateRange.to) filters.created_to = dateRange.to;
    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const loadUsers = async (page: number = 1, limit: number = 25, searchTerm: string = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getUserList(page, limit, searchTerm, buildFilters());
      setUsers(response.users);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(pagination.page, pagination.limit, search);
  }, [pagination.page, pagination.limit, search, subscriptionFilter, providerFilter, statusFilter, createdFilter, createdRange?.from?.getTime(), createdRange?.to?.getTime()]);

  useEffect(() => {
    if (refreshTrigger === 0) return;
    const refreshSilently = async () => {
      try {
        const response = await apiService.getUserList(pagination.page, pagination.limit, search, buildFilters());
        setUsers(response.users);
        setPagination(response.pagination);
      } catch { /* keep stale data visible on error */ }
    };
    refreshSilently();
  }, [refreshTrigger]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPagination({ ...pagination, page: 1 });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleUserClick = (user: UserListItem) => {
    onUserSelect(user);
  };

  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({ ...pagination, limit: newLimit, page: 1 });
  };

  const toggleSSNVisibility = async (userId: string) => {
    // If hiding, just toggle off
    if (visibleSSNs.has(userId)) {
      setVisibleSSNs(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      return;
    }

    // If showing and we don't have the full SSN yet, fetch it
    if (!fullSSNs.has(userId)) {
      setLoadingSSNs(prev => new Set(prev).add(userId));
      try {
        const response = await apiService.getUserSSN(userId);
        if (response.success && response.data.ssn) {
          setFullSSNs(prev => new Map(prev).set(userId, response.data.ssn));
        }
      } catch (err) {
        console.error('Failed to fetch SSN:', err);
        setError(err instanceof ApiError ? err.message : 'Failed to load SSN');
        setLoadingSSNs(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        return;
      } finally {
        setLoadingSSNs(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      }
    }

    // Show the SSN
    setVisibleSSNs(prev => new Set(prev).add(userId));
  };

  const handleEnrollToCRS = async (user: UserListItem) => {
    // Clear any previous error
    setEnrollmentError(null);
    setEnrollingUserId(user.id);

    try {
      const response = await apiService.enrollUserToCRS(user.id);

      if (response.success) {
        // Show success badge
        setEnrollmentSuccess(prev => {
          const newSet = new Set(prev);
          newSet.add(user.id);
          return newSet;
        });

        // Refresh user list to get updated data
        await loadUsers(pagination.page, pagination.limit, search);

        // Clear success badge after 3 seconds
        setTimeout(() => {
          setEnrollmentSuccess(prev => {
            const newSet = new Set(prev);
            newSet.delete(user.id);
            return newSet;
          });
        }, 3000);
      }
    } catch (err) {
      const error = err as ApiError;
      
      // Handle different status codes
      if (error.status === 409) {
        // User already enrolled - show info message and refresh data
        setEnrollmentError({
          userId: user.id,
          message: error.message || 'User is already enrolled in CRS',
        });
        await loadUsers(pagination.page, pagination.limit, search);
      } else {
        // Other errors
        setEnrollmentError({
          userId: user.id,
          message: error.message || 'Failed to enroll user to CRS',
        });
      }

      // Clear error after 5 seconds
      setTimeout(() => {
        setEnrollmentError(null);
      }, 5000);
    } finally {
      setEnrollingUserId(null);
    }
  };

  const handleMigrateUserToCRS = async (user: UserListItem) => {
    // Clear any previous error
    setMigrationError(null);
    setMigratingUserId(user.id);

    try {
      const response = await apiService.migrateUserToCRS(user.id);

      if (response.success) {
        // Show success badge
        setMigrationSuccess(prev => {
          const newSet = new Set(prev);
          newSet.add(user.id);
          return newSet;
        });

        // Refresh user list to get updated data
        await loadUsers(pagination.page, pagination.limit, search);

        // Clear success badge after 3 seconds
        setTimeout(() => {
          setMigrationSuccess(prev => {
            const newSet = new Set(prev);
            newSet.delete(user.id);
            return newSet;
          });
        }, 3000);
      }
    } catch (err) {
      const error = err as ApiError;
      
      setMigrationError({
        userId: user.id,
        message: error.message || 'Failed to migrate user to CRS',
      });

      // Clear error after 5 seconds
      setTimeout(() => {
        setMigrationError(null);
      }, 5000);
    } finally {
      setMigratingUserId(null);
    }
  };

  const handleCRSRegistration = async () => {
    // Validate required fields
    if (!crsFormData.email || !crsFormData.ssn || !crsFormData.birthDate || !crsFormData.phoneNumber) {
      setRegistrationError('Please fill in all required fields');
      return;
    }
    const referralIdError = validateReferralId(crsFormData.referralId);
    if (referralIdError) {
      setRegistrationError(referralIdError);
      return;
    }

    setIsRegistering(true);
    setRegistrationError(null);
    setRegistrationFieldErrors([]);
    setRegistrationSuccess(false);
    setVerificationStatus(null);

    try {
      // Convert birth date from YYYY-MM-DD to MM/DD/YYYY
      const [year, month, day] = crsFormData.birthDate.split('-');
      const formattedBirthDate = `${month}/${day}/${year}`;

      const response = await apiService.registerCRSUser({
        ...crsFormData,
        referralId: crsFormData.referralId.trim() || undefined,
        birthDate: formattedBirthDate,
      });

      // Store user's access token for verification polling
      if (response.accessToken) {
        setUserAccessToken(response.accessToken);
      }

      // Check verification status from response
      const verification = (response as any).verification;
      
      if (verification) {
        setVerificationStatus(verification);
        
        if (verification.idpass) {
          // Identity verified immediately
          setRegistrationSuccess(true);
          setTimeout(() => {
            loadUsers(pagination.page, pagination.limit, search);
            setShowCRSDialog(false);
            resetCRSForm();
            setRegistrationSuccess(false);
            setVerificationStatus(null);
          }, 2000);
        } else if (verification.requiresSMFA && verification.smfaToken) {
          // Start polling for SMS verification
          startSMFAPolling(verification.smfaToken, response.accessToken);
        }
      } else {
        // No verification object, treat as success
        setRegistrationSuccess(true);
        setTimeout(() => {
          loadUsers(pagination.page, pagination.limit, search);
          setShowCRSDialog(false);
          resetCRSForm();
          setRegistrationSuccess(false);
        }, 1500);
      }
    } catch (err) {
      const error = err as ApiError;
      setRegistrationError(error.message || 'Failed to register CRS user');
      if (error.fieldErrors?.length) {
        setRegistrationFieldErrors(error.fieldErrors);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const startSMFAPolling = (smfaToken: string, accessToken: string) => {
    setIsPolling(true);
    let pollCount = 0;
    const maxPolls = 10;
    
    const interval = setInterval(async () => {
      pollCount++;
      
      // Stop polling after max attempts
      if (pollCount > maxPolls) {
        clearInterval(interval);
        setPollingInterval(null);
        setIsPolling(false);
        setRegistrationError('SMS verification timed out after 10 attempts. Please try again.');
        setVerificationStatus(null);
        return;
      }
      
      try {
        // Use the user's access token for the request
        const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1'}/alt/check-smfa-status`;
        const backofficeVersion = await getBackofficeVersion();
        
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'x-cb-source': 'backoffice',
          'x-cb-version': backofficeVersion,
        };

        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
        if (publicSecret) {
          headers['x-cb-auth'] = publicSecret;
        }

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ smfaToken }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        
        if (result.verified && result.idpass) {
          // Verification complete
          clearInterval(interval);
          setPollingInterval(null);
          setIsPolling(false);
          setRegistrationSuccess(true);
          setVerificationStatus({
            idpass: true,
            requiresSMFA: false,
            message: 'Verification complete!',
          });
          
          setTimeout(() => {
            loadUsers(pagination.page, pagination.limit, search);
            setShowCRSDialog(false);
            resetCRSForm();
            setRegistrationSuccess(false);
            setVerificationStatus(null);
            setUserAccessToken(null);
          }, 2000);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds

    setPollingInterval(interval);

    // Stop polling after 5 minutes
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPollingInterval(null);
        setIsPolling(false);
        setRegistrationError('Verification timeout. User created but verification incomplete.');
      }
    }, 300000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const resetCRSForm = () => {
    setCrsFormData({
      email: '',
      firstName: '',
      lastName: '',
      birthDate: '',
      ssn: '',
      streetAddress: '',
      city: '',
      state: '',
      zip: '',
      phoneNumber: '',
      referralId: '',
      goal: 'improve_score',
      goalTargetPeriod: '6_months',
    });
    setRegistrationError(null);
    setRegistrationFieldErrors([]);
    setRegistrationSuccess(false);
    setVerificationStatus(null);
    setIsPolling(false);
    setUserAccessToken(null);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const openCRSDialog = () => {
    resetCRSForm();
    setCrsExampleIndex(0); // Reset to first example
    setShowCRSDialog(true);
  };

  const prefillTestData = () => {
    const example = crsExamples[crsExampleIndex];
    setCrsFormData({
      email: example.email,
      firstName: example.firstName,
      lastName: example.lastName,
      birthDate: example.birthDate,
      ssn: example.ssn,
      streetAddress: example.streetAddress,
      city: example.city,
      state: example.state,
      zip: example.zip,
      phoneNumber: example.phoneNumber,
      referralId: '',
      goal: 'improve_score',
      goalTargetPeriod: '6_months',
    });
    // Move to next example for next click
    setCrsExampleIndex((prev) => (prev + 1) % crsExamples.length);
  };

  const handleCRSFormChange = (field: string, value: string) => {
    setCrsFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (registrationFieldErrors.some(e => e.field === field)) {
      setRegistrationFieldErrors(prev => prev.filter(e => e.field !== field));
    }
  };

  const getCrsFieldError = (field: string): string | undefined =>
    registrationFieldErrors.find(e => e.field === field)?.message;
  
  // IdentityIQ Dialog Handlers
  const openIdentityIQDialog = () => {
    resetIdentityIQForm();
    setIdentityIQExampleIndex(0); // Reset to first example when opening dialog
    setShowIdentityIQDialog(true);
  };
  
  const resetIdentityIQForm = () => {
    setIdentityIQFormData({
      email: '',
      firstName: '',
      lastName: '',
      birthDate: '',
      ssn: '',
      streetAddress: '',
      city: '',
      state: '',
      zip: '',
      phoneNumber: '',
      referralId: '',
      goal: 'improve_score',
      goalTargetPeriod: '6_months',
    });
    setIdentityIQError(null);
    setIdentityIQFieldErrors([]);
    setIdentityIQSuccess(false);
  };
  
  const prefillIdentityIQTestData = () => {
    const example = identityIQExamples[identityIQExampleIndex];
    
    // Convert date format from M/D/YY to YYYY-MM-DD
    const [month, day, year] = example.birthDate.split('/');
    const fullYear = year.length === 2 ? (parseInt(year) < 50 ? `20${year}` : `19${year}`) : year;
    const formattedBirthDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    
    setIdentityIQFormData({
      email: example.email,
      firstName: example.firstName,
      lastName: example.lastName,
      birthDate: formattedBirthDate,
      ssn: example.ssn,
      streetAddress: example.streetAddress,
      city: example.city,
      state: example.state,
      zip: example.zip,
      phoneNumber: '5555551234', // Default phone number
      referralId: '',
      goal: 'improve_score',
      goalTargetPeriod: '6_months',
    });
    
    // Move to next example (cycle back to 0 if at the end)
    setIdentityIQExampleIndex((prev) => (prev + 1) % identityIQExamples.length);
  };
  
  const handleIdentityIQFormChange = (field: string, value: string) => {
    setIdentityIQFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (identityIQFieldErrors.some(e => e.field === field)) {
      setIdentityIQFieldErrors(prev => prev.filter(e => e.field !== field));
    }
  };

  const getIiqFieldError = (field: string): string | undefined =>
    identityIQFieldErrors.find(e => e.field === field)?.message;
  
  const handleIdentityIQRegistration = async () => {
    // Validate required fields
    if (!identityIQFormData.email || !identityIQFormData.ssn || !identityIQFormData.birthDate || !identityIQFormData.phoneNumber) {
      setIdentityIQError('Please fill in all required fields');
      return;
    }
    const referralIdError = validateReferralId(identityIQFormData.referralId);
    if (referralIdError) {
      setIdentityIQError(referralIdError);
      return;
    }

    setIsRegisteringIdentityIQ(true);
    setIdentityIQError(null);
    setIdentityIQFieldErrors([]);
    setIdentityIQSuccess(false);

    try {
      // Convert birth date from YYYY-MM-DD to MM/DD/YYYY
      const [year, month, day] = identityIQFormData.birthDate.split('-');
      const formattedBirthDate = `${month}/${day}/${year}`;

      await apiService.registerIdentityIQUser({
        ...identityIQFormData,
        referralId: identityIQFormData.referralId.trim() || undefined,
        birthDate: formattedBirthDate,
      });

      setIdentityIQSuccess(true);
      setTimeout(() => {
        loadUsers(pagination.page, pagination.limit, search);
        setShowIdentityIQDialog(false);
        resetIdentityIQForm();
        setIdentityIQSuccess(false);
      }, 1500);
    } catch (err) {
      const error = err as ApiError;
      setIdentityIQError(error.message || 'Failed to register IdentityIQ user');
      if (error.fieldErrors?.length) {
        setIdentityIQFieldErrors(error.fieldErrors);
      }
    } finally {
      setIsRegisteringIdentityIQ(false);
    }
  };

  const getProviderBadge = (user: UserListItem) => {
    if (user.crsUserId) {
      return 'CRS';
    }
    if (user.membershipNo) {
      return 'Identity IQ';
    }
    return '-';
  };

  const formatSSN = (userId: string, ssnLast4: string | null, isVisible: boolean) => {
    if (!ssnLast4) return '-';
    
    if (isVisible) {
      const fullSSN = fullSSNs.get(userId);
      if (fullSSN) {
        // Format full SSN as XXX-XX-XXXX
        if (fullSSN.length === 9) {
          return `${fullSSN.slice(0, 3)}-${fullSSN.slice(3, 5)}-${fullSSN.slice(5)}`;
        }
        return fullSSN;
      }
      // Fallback to last 4 if full SSN not available
      return `•••-••-${ssnLast4}`;
    }
    return '•••-••-••••';
  };

  const getSubscriptionBadge = (type: string) => {
    const badges = {
      FREE: {
        label: 'Free',
        className: 'border border-gray-300 text-gray-700 bg-white'
      },
      PRO_ACCESS: {
        label: 'Pro',
        className: 'border border-purple-300 text-purple-700 bg-white'
      },
      FULL_ACCESS: {
        label: 'Max',
        className: 'bg-purple-600 text-white border-purple-600'
      }
    };

    const badge = badges[type as keyof typeof badges] || badges.FREE;

    return (
      <span className={`inline-flex items-center px-2.5 h-6 rounded-lg text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">User Management</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              className="bg-black hover:bg-black/90 text-white rounded-lg font-normal px-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add user
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={openCRSDialog}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add CRS User
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openIdentityIQDialog}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Identity IQ User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
        {/* Search Input */}
        <div className="relative w-full sm:w-[420px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name, phone, email, or SSN..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9 h-10 rounded-lg w-full"
          />
        </div>
        
        {/* Filters */}
        <div className="flex gap-2 flex-wrap sm:flex-nowrap sm:ml-auto">
          <Select value={subscriptionFilter} onValueChange={(v) => { setSubscriptionFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
            <SelectTrigger className="min-w-[150px] w-auto h-10 rounded-lg">
              <span className="text-sm whitespace-nowrap">
                <span className="text-gray-500">Subscriptions: </span>
                <span className="font-medium">
                  {subscriptionFilter === 'all' ? 'All' : subscriptionFilter.charAt(0).toUpperCase() + subscriptionFilter.slice(1)}
                </span>
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="max">Max</SelectItem>
            </SelectContent>
          </Select>

          <Select value={providerFilter} onValueChange={(v) => { setProviderFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
            <SelectTrigger className="min-w-[140px] w-auto h-10 rounded-lg">
              <span className="text-sm whitespace-nowrap">
                <span className="text-gray-500">Providers: </span>
                <span className="font-medium">
                  {providerFilter === 'all' ? 'All' : providerFilter === 'crs' ? 'CRS' : 'Identity IQ'}
                </span>
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="crs">CRS</SelectItem>
              <SelectItem value="identityiq">Identity IQ</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
            <SelectTrigger className="min-w-[130px] w-auto h-10 rounded-lg">
              <span className="text-sm whitespace-nowrap">
                <span className="text-gray-500">Statuses: </span>
                <span className="font-medium">
                  {statusFilter === 'all' ? 'All' : statusFilter === 'not_enrolled' ? 'Not Enrolled' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                </span>
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="enrolled">Enrolled</SelectItem>
              <SelectItem value="not_enrolled">Not Enrolled</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
            </SelectContent>
          </Select>

          {createdFilter === 'custom' && (
            <CreatedDateRangePicker
              value={createdRange}
              showLabel={false}
              className="min-w-0 w-0"
              onChange={setCreatedRange}
              open={isCreatedPickerOpen}
              onOpenChange={setIsCreatedPickerOpen}
              hideTrigger
            />
          )}

          <Select
            value={createdFilter}
            onValueChange={(value) => {
              setCreatedFilter(value);
              setPagination(p => ({ ...p, page: 1 }));
              if (value === 'custom') {
                setIsCreatedPickerOpen(true);
              } else {
                setCreatedRange(undefined);
                setIsCreatedPickerOpen(false);
              }
            }}
          >
            <SelectTrigger
              className="min-w-[220px] w-auto h-10 rounded-lg"
              onPointerDown={(e) => {
                if (createdFilter === 'custom') {
                  e.preventDefault();
                  setIsCreatedPickerOpen(true);
                }
              }}
            >
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">
                  <span className="text-gray-500">Created: </span>
                  <span className="font-medium">
                    {createdFilter === 'all' ? 'All period' : 
                     createdFilter === 'today' ? 'Today' : 
                     createdFilter === 'week' ? 'This week' :
                     createdFilter === 'month' ? 'This month' :
                     formatCreatedRangeLabel(createdRange)}
                  </span>
                </span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="custom">Custom range</SelectItem>
              <SelectItem value="all">All period</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="month">This month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && users.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No users found</p>
        </div>
      ) : (
        <>
          {/* User Table */}
          <div className="border border-border rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-gray-200">
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Name</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Phone</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Email</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Subscription</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Provider</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Source</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Status</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Created</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <TableRow
                      onClick={() => handleUserClick(user)}
                      className={cn(
                        "h-14 hover:bg-gray-50 border-b border-gray-200 cursor-pointer",
                        user.isDeleted && 'opacity-50'
                      )}
                    >
                      <TableCell className="py-2 px-4 font-medium text-sm text-gray-900">
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell className="py-2 px-4 font-medium text-sm text-gray-900">{user.phoneNumber}</TableCell>
                      <TableCell className="py-2 px-4 font-medium text-sm text-gray-600">{user.email || '-'}</TableCell>
                      <TableCell className="py-2 px-4">
                        <div className="flex items-center gap-1.5">
                          {getSubscriptionBadge(user.subscriptionType)}
                          {user.isTrial && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-lg border border-blue-300 bg-blue-50 text-xs font-medium text-blue-700 cursor-default"
                              title={user.trialExpiresAt ? `Trial ends ${new Date(user.trialExpiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Trial active'}
                            >
                              Trial
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-4 font-medium text-sm text-gray-900">
                        {user.crsUserId ? 'CRS' : user.membershipNo ? 'Identity IQ' : '-'}
                      </TableCell>
                      <TableCell className="py-2 px-4 text-sm text-gray-600">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium text-gray-900">{user.cbSource || 'ios'}</span>
                          {user.referralId && <span className="text-xs text-gray-400">ref: {user.referralId}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-4">
                        <div className="flex flex-wrap gap-1">
                          {user.enrolled && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg border border-gray-300 bg-white">
                              <CheckCircle className="h-3.5 w-3.5 text-gray-900" />
                              <span className="text-xs font-medium text-gray-900">Enrolled</span>
                            </span>
                          )}
                          {enrollingUserId === user.id && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg border border-orange-300 bg-orange-50">
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-orange-600" />
                              <span className="text-xs font-medium text-orange-600">Enrolling</span>
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-4 font-medium text-sm text-gray-600">
                        {formatDate(user.createdAt)}
                      </TableCell>
                      <TableCell className="py-2 px-4">
                        <DropdownMenu 
                          open={openDropdownId === user.id}
                          onOpenChange={(open: boolean) => setOpenDropdownId(open ? user.id : null)}
                        >
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 hover:bg-gray-100"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4 text-gray-600" />
                            </Button>
                          </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[150px]">
                              <DropdownMenuItem
                                onClick={() => handleUserClick(user)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Login as User
                              </DropdownMenuItem>
                              {/* Enroll to CRS - Only show if user is not enrolled in CRS */}
                              {!user.crsUserId && (
                                enrollmentSuccess.has(user.id) ? (
                                  <div className="px-2 py-1.5 text-sm text-green-600 cursor-default">
                                    Enrolled to CRS
                                  </div>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={enrollingUserId === user.id ? undefined : () => handleEnrollToCRS(user)}
                                    className={enrollingUserId === user.id ? 'opacity-50 cursor-not-allowed' : ''}
                                  >
                                    {enrollingUserId === user.id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Enrolling...
                                      </>
                                    ) : (
                                      <>
                                        <UserCheck className="h-4 w-4 mr-2" />
                                        Enroll to CRS
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )
                              )}
                              
                              {/* Migrate to CRS - Only show for IdentityIQ users */}
                              {user.provider === 'identityiq' && (
                                migrationSuccess.has(user.id) ? (
                                  <div className="px-2 py-1.5 text-sm text-green-600 cursor-default">
                                    Migrated to CRS
                                  </div>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={migratingUserId === user.id ? undefined : () => handleMigrateUserToCRS(user)}
                                    className={migratingUserId === user.id ? 'opacity-50 cursor-not-allowed' : ''}
                                  >
                                    {migratingUserId === user.id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Migrating...
                                      </>
                                    ) : (
                                      <>
                                        <ArrowRightLeft className="h-4 w-4 mr-2" />
                                        Migrate to CRS
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                      </TableCell>
                    </TableRow>
                      {/* Show enrollment error for this specific user under the row */}
                      {enrollmentError && enrollmentError.userId === user.id && (
                        <TableRow>
                          <TableCell colSpan={10} className="bg-red-50 border-l-4 border-red-500 py-2">
                            <div className="flex items-start gap-2 text-red-700">
                              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span className="text-sm">{enrollmentError.message}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Show migration error for this specific user under the row */}
                      {migrationError && migrationError.userId === user.id && (
                        <TableRow>
                          <TableCell colSpan={10} className="bg-red-50 border-l-4 border-red-500 py-2">
                            <div className="flex items-start gap-2 text-red-700">
                              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span className="text-sm">{migrationError.message}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {(pagination.page - 1) * pagination.limit + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
            </div>

            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1 || loading}
                className="h-9 text-sm"
              >
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={cn(
                        "h-9 w-9 text-sm rounded-lg",
                        pagination.page === pageNum 
                          ? "border border-gray-300 bg-white text-gray-900 font-medium hover:bg-gray-50" 
                          : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                
                {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                  <>
                    <span className="px-2 text-muted-foreground">...</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={loading}
                      className="h-9 w-9 text-sm"
                    >
                      {pagination.totalPages}
                    </Button>
                  </>
                )}
              </div>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="h-9 text-sm"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* CRS Registration Dialog */}
      <Dialog open={showCRSDialog} onOpenChange={setShowCRSDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New CRS User</DialogTitle>
            <DialogDescription>
              Register a new user with the CRS credit provider.
              <button
                type="button"
                onClick={prefillTestData}
                className="block mt-2 text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
              >
                Prefill example data
              </button>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {registrationSuccess ? (
              <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <div>
                  <p className="text-sm font-medium text-green-900 dark:text-green-100">User Created Successfully!</p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                    {verificationStatus?.idpass 
                      ? 'New CRS user has been registered and verified.'
                      : 'New CRS user has been registered.'}
                  </p>
                </div>
              </div>
            ) : isPolling && verificationStatus?.requiresSMFA ? (
              <div className="space-y-4">
                <div className="flex items-start gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">SMS Verification Required</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      {verificationStatus.smsMessage || verificationStatus.message || 'Please check the user\'s phone for verification link.'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">First Name *</label>
                    <Input
                      value={crsFormData.firstName}
                      onChange={(e) => handleCRSFormChange('firstName', e.target.value)}
                      placeholder="John"
                      className={getCrsFieldError('firstName') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('firstName') && <p className="text-xs text-red-600">{getCrsFieldError('firstName')}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Last Name *</label>
                    <Input
                      value={crsFormData.lastName}
                      onChange={(e) => handleCRSFormChange('lastName', e.target.value)}
                      placeholder="Doe"
                      className={getCrsFieldError('lastName') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('lastName') && <p className="text-xs text-red-600">{getCrsFieldError('lastName')}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email *</label>
                    <Input
                      type="email"
                      value={crsFormData.email}
                      onChange={(e) => handleCRSFormChange('email', e.target.value)}
                      placeholder="john.doe@example.com"
                      className={getCrsFieldError('email') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('email') && <p className="text-xs text-red-600">{getCrsFieldError('email')}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Phone Number *</label>
                    <Input
                      value={crsFormData.phoneNumber}
                      onChange={(e) => handleCRSFormChange('phoneNumber', e.target.value)}
                      placeholder="5555551234"
                      className={getCrsFieldError('phoneNumber') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('phoneNumber') && <p className="text-xs text-red-600">{getCrsFieldError('phoneNumber')}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Referral ID</label>
                  <Input
                    value={crsFormData.referralId}
                    onChange={(e) => handleCRSFormChange('referralId', e.target.value)}
                    placeholder="abc-123"
                    maxLength={64}
                  />
                  <p className="text-xs text-gray-500">Optional, letters/numbers/dashes only</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Birth Date *</label>
                    <Input
                      type="date"
                      value={crsFormData.birthDate}
                      onChange={(e) => handleCRSFormChange('birthDate', e.target.value)}
                      className={getCrsFieldError('birthDate') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('birthDate') && <p className="text-xs text-red-600">{getCrsFieldError('birthDate')}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">SSN *</label>
                    <Input
                      value={crsFormData.ssn}
                      onChange={(e) => handleCRSFormChange('ssn', e.target.value)}
                      placeholder="123456789"
                      maxLength={9}
                      className={getCrsFieldError('ssn') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('ssn') && <p className="text-xs text-red-600">{getCrsFieldError('ssn')}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Street Address *</label>
                  <Input
                    value={crsFormData.streetAddress}
                    onChange={(e) => handleCRSFormChange('streetAddress', e.target.value)}
                    placeholder="123 Main St"
                    className={getCrsFieldError('streetAddress') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  />
                  {getCrsFieldError('streetAddress') && <p className="text-xs text-red-600">{getCrsFieldError('streetAddress')}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">City *</label>
                    <Input
                      value={crsFormData.city}
                      onChange={(e) => handleCRSFormChange('city', e.target.value)}
                      placeholder="Atlanta"
                      className={getCrsFieldError('city') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('city') && <p className="text-xs text-red-600">{getCrsFieldError('city')}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">State *</label>
                    <Input
                      value={crsFormData.state}
                      onChange={(e) => handleCRSFormChange('state', e.target.value)}
                      placeholder="GA"
                      maxLength={2}
                      className={getCrsFieldError('state') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('state') && <p className="text-xs text-red-600">{getCrsFieldError('state')}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">ZIP Code *</label>
                    <Input
                      value={crsFormData.zip}
                      onChange={(e) => handleCRSFormChange('zip', e.target.value)}
                      placeholder="30316"
                      maxLength={5}
                      className={getCrsFieldError('zip') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {getCrsFieldError('zip') && <p className="text-xs text-red-600">{getCrsFieldError('zip')}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Goal</label>
                    <select
                      value={crsFormData.goal}
                      onChange={(e) => handleCRSFormChange('goal', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    >
                      <option value="improve_score">Improve Score</option>
                      <option value="get_loan">Get Loan</option>
                      <option value="buy_home">Buy Home</option>
                      <option value="buy_car">Buy Car</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Target Period</label>
                    <select
                      value={crsFormData.goalTargetPeriod}
                      onChange={(e) => handleCRSFormChange('goalTargetPeriod', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    >
                      <option value="3_months">3 Months</option>
                      <option value="6_months">6 Months</option>
                      <option value="12_months">12 Months</option>
                      <option value="24_months">24 Months</option>
                    </select>
                  </div>
                </div>

                {registrationError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-900 dark:text-red-100">Registration Failed</p>
                        <p className="text-xs text-red-700 dark:text-red-300 mt-1">{registrationError}</p>
                        {registrationFieldErrors.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {registrationFieldErrors.map((fe, i) => (
                              <li key={i} className="text-xs text-red-700 dark:text-red-300">
                                <span className="font-medium">{fe.field}:</span> {fe.message}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            {!registrationSuccess && !isPolling && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowCRSDialog(false)}
                  disabled={isRegistering}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCRSRegistration}
                  disabled={isRegistering}
                >
                  {isRegistering ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating User...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add CRS User
                    </>
                  )}
                </Button>
              </>
            )}
            {isPolling && (
              <Button
                variant="outline"
                onClick={() => {
                  if (pollingInterval) clearInterval(pollingInterval);
                  setShowCRSDialog(false);
                  resetCRSForm();
                }}
              >
                Close (User Created)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IdentityIQ Registration Dialog */}
      <Dialog open={showIdentityIQDialog} onOpenChange={setShowIdentityIQDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add IdentityIQ User</DialogTitle>
            <DialogDescription>
              Register a new user with the IdentityIQ credit provider.
              <button
                type="button"
                onClick={prefillIdentityIQTestData}
                className="block mt-2 text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
              >
                Prefill example data ({identityIQExampleIndex + 1}/{identityIQExamples.length})
              </button>
            </DialogDescription>
          </DialogHeader>

          {identityIQError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900">Registration Failed</p>
                  <p className="text-xs text-red-700 mt-1">{identityIQError}</p>
                  {identityIQFieldErrors.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {identityIQFieldErrors.map((fe, i) => (
                        <li key={i} className="text-xs text-red-700">
                          <span className="font-medium">{fe.field}:</span> {fe.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {identityIQSuccess && (
            <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>IdentityIQ user created successfully!</span>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  value={identityIQFormData.email}
                  onChange={(e) => handleIdentityIQFormChange('email', e.target.value)}
                  placeholder="user@example.com"
                  className={getIiqFieldError('email') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('email') && <p className="text-xs text-red-600">{getIiqFieldError('email')}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone Number *</label>
                <Input
                  type="tel"
                  value={identityIQFormData.phoneNumber}
                  onChange={(e) => handleIdentityIQFormChange('phoneNumber', e.target.value)}
                  placeholder="5555551234"
                  className={getIiqFieldError('phoneNumber') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('phoneNumber') && <p className="text-xs text-red-600">{getIiqFieldError('phoneNumber')}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Referral ID</label>
              <Input
                value={identityIQFormData.referralId}
                onChange={(e) => handleIdentityIQFormChange('referralId', e.target.value)}
                placeholder="abc-123"
                maxLength={64}
              />
              <p className="text-xs text-gray-500">Optional, letters/numbers/dashes only</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  value={identityIQFormData.firstName}
                  onChange={(e) => handleIdentityIQFormChange('firstName', e.target.value)}
                  placeholder="John"
                  className={getIiqFieldError('firstName') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('firstName') && <p className="text-xs text-red-600">{getIiqFieldError('firstName')}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name *</label>
                <Input
                  value={identityIQFormData.lastName}
                  onChange={(e) => handleIdentityIQFormChange('lastName', e.target.value)}
                  placeholder="Doe"
                  className={getIiqFieldError('lastName') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('lastName') && <p className="text-xs text-red-600">{getIiqFieldError('lastName')}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Birth Date *</label>
                <Input
                  type="date"
                  value={identityIQFormData.birthDate}
                  onChange={(e) => handleIdentityIQFormChange('birthDate', e.target.value)}
                  className={getIiqFieldError('birthDate') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('birthDate') && <p className="text-xs text-red-600">{getIiqFieldError('birthDate')}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">SSN *</label>
                <Input
                  value={identityIQFormData.ssn}
                  onChange={(e) => handleIdentityIQFormChange('ssn', e.target.value)}
                  placeholder="123456789"
                  maxLength={9}
                  className={getIiqFieldError('ssn') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('ssn') && <p className="text-xs text-red-600">{getIiqFieldError('ssn')}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Street Address *</label>
              <Input
                value={identityIQFormData.streetAddress}
                onChange={(e) => handleIdentityIQFormChange('streetAddress', e.target.value)}
                placeholder="123 Main Street"
                className={getIiqFieldError('streetAddress') ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {getIiqFieldError('streetAddress') && <p className="text-xs text-red-600">{getIiqFieldError('streetAddress')}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">City *</label>
                <Input
                  value={identityIQFormData.city}
                  onChange={(e) => handleIdentityIQFormChange('city', e.target.value)}
                  placeholder="Atlanta"
                  className={getIiqFieldError('city') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('city') && <p className="text-xs text-red-600">{getIiqFieldError('city')}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">State *</label>
                <Input
                  value={identityIQFormData.state}
                  onChange={(e) => handleIdentityIQFormChange('state', e.target.value)}
                  placeholder="GA"
                  maxLength={2}
                  className={getIiqFieldError('state') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('state') && <p className="text-xs text-red-600">{getIiqFieldError('state')}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">ZIP Code *</label>
                <Input
                  value={identityIQFormData.zip}
                  onChange={(e) => handleIdentityIQFormChange('zip', e.target.value)}
                  placeholder="30316"
                  maxLength={5}
                  className={getIiqFieldError('zip') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {getIiqFieldError('zip') && <p className="text-xs text-red-600">{getIiqFieldError('zip')}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Goal</label>
                <select
                  value={identityIQFormData.goal}
                  onChange={(e) => handleIdentityIQFormChange('goal', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="improve_score">Improve Score</option>
                  <option value="get_loan">Get Loan</option>
                  <option value="buy_home">Buy Home</option>
                  <option value="buy_car">Buy Car</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Target Period</label>
                <select
                  value={identityIQFormData.goalTargetPeriod}
                  onChange={(e) => handleIdentityIQFormChange('goalTargetPeriod', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="3_months">3 Months</option>
                  <option value="6_months">6 Months</option>
                  <option value="12_months">12 Months</option>
                  <option value="24_months">24 Months</option>
                </select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowIdentityIQDialog(false)}
              disabled={isRegisteringIdentityIQ}
            >
              Cancel
            </Button>
            <Button
              onClick={handleIdentityIQRegistration}
              disabled={isRegisteringIdentityIQ}
            >
              {isRegisteringIdentityIQ ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating User...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add IdentityIQ User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

